import type {
  AcceptInviteResponse,
  CreateOrganizationResponse,
  InviteMemberResponse,
  InvitePreview,
  OrganizationContext,
  OrganizationMemberAccessState,
  RedeemInviteResponse,
  TeamBillingResult,
  TeamContinuationResponse,
  UpdateMemberAccessResponse,
} from '@kiao-voice/api-client'
import { ApiError, type ApiClient } from '@kiao-voice/api-client'
import {
  createWebApiClient,
  createWebPublicApiClient,
  getWebApiClient,
  getWebPublicApiClient,
} from '../../lib/web-api-client'
import type {
  PurchaseTrialSeatsRequest,
  SubscribeTeamRequest,
  SwitchableContext,
  TeamPlanPricing,
  TrialSeatQuote,
  TrialSeatQuoteQuery,
  UpdateMemberAccessRequest,
} from '@kiao-voice/api-contract/organization'
import type {
  PaymentDeliveryStatusResponse,
  TeamSubscriptionPaymentStatusResponse,
} from '@kiao-voice/api-contract/billing'
import type {
  EmailVerificationRequestResponse,
  EmailVerificationVerifyResponse,
} from '@kiao-voice/api-contract/email-verification'

export type { SwitchableContext, TeamPlanPricing, TrialSeatQuote }

/**
 * Client input spec is STRICTER than the wire contract: the server tolerates a
 * missing `name` (owner-email fallback), but this checkout flow must always
 * send one — keep it required so refactoring it away fails compilation.
 */
export type SubscribeTeamPayload = SubscribeTeamRequest & { name: string }

export class TeamApiError extends Error {
  readonly status: number
  /** Machine error code from the API body (e.g. 'seat_limit_reached'). */
  readonly code: string | null
  readonly body: unknown
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Team API error ${status}`)
    this.name = 'TeamApiError'
    this.status = status
    this.code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : null
    this.body = body
  }
}

/** What an API error code means to the team UI. */
export type TeamErrorKind =
  | 'SeatLimit'
  | 'AlreadyMember'
  | 'NotOwner'
  | 'NoCard'
  | 'SeatsBelowUsage'
  | 'NoActiveTeam'
  | 'BillingPeriodEnded'
  | 'PaymentRecovery'
  | 'PreviousPaymentRefunding'
  | 'PaymentNeedsSupport'
  | 'NotPaidSubscription'
  | 'NotTrial'
  | 'TrialActive'
  | 'CannotDisableOwner'
  | 'MemberRevoked'
  | 'MemberNotFound'
  | 'MemberDisabled'
  | 'Generic'

export function teamErrorKind(code: string): TeamErrorKind {
  switch (code) {
    case 'seat_limit_reached':
      return 'SeatLimit'
    case 'already_member':
      return 'AlreadyMember'
    case 'not_team_owner':
      return 'NotOwner'
    case 'no_card_on_file':
      return 'NoCard'
    case 'seats_below_current_usage':
      return 'SeatsBelowUsage'
    case 'no_active_team_subscription':
      return 'NoActiveTeam'
    case 'billing_period_ended':
      return 'BillingPeriodEnded'
    case 'team_member_disabled':
      return 'MemberDisabled'
    case 'previous_payment_refunding':
      return 'PreviousPaymentRefunding'
    case 'payment_needs_support':
      return 'PaymentNeedsSupport'
    case 'payment_recovery_pending':
    case 'stale_seat_payment_refunded':
    case 'checkout_intent_conflict':
    case 'pending_3ds_conflict':
      return 'PaymentRecovery'
    case 'not_paid_subscription':
      return 'NotPaidSubscription'
    case 'not_trial':
      return 'NotTrial'
    case 'trial_active':
      return 'TrialActive'
    case 'cannot_disable_owner':
      return 'CannotDisableOwner'
    case 'member_revoked':
      return 'MemberRevoked'
    case 'member_not_found':
      return 'MemberNotFound'
    default:
      return 'Generic'
  }
}

/** The `team.err*` i18n key for an API error code; unknown or missing codes get the generic one. */
export function teamErrorMessageKey(code: string | null | undefined): string {
  return `team.err${teamErrorKind(code ?? '')}`
}

/**
 * The message a seat-payment refusal in the Team summary shows next to the
 * disabled control (the same one the endpoint answers with), or null when the
 * refusal is not about a seat payment.
 */
export function seatPaymentRefusalMessageKey(reason: string | null | undefined): string | null {
  switch (reason) {
    case 'payment_recovery_pending':
    case 'previous_payment_refunding':
    case 'payment_needs_support':
      return teamErrorMessageKey(reason)
    default:
      return null
  }
}

/**
 * Per-call options. Bearer token + `X-Active-Context` are injected by the shared
 * web client from storage (#223); the 403 `active_context_forbidden` self-heal
 * also lives there now. `fetchFn` is a test seam; `signal` cancels the request.
 */
interface RequestOptions {
  fetchFn?: typeof fetch
  signal?: AbortSignal
}

/** Map a thrown `ApiError` to `TeamApiError` (which re-derives `code` from the
 *  body). Non-ApiError values (network failures) propagate unchanged. */
function toTeamError(error: unknown): unknown {
  if (error instanceof ApiError) {
    const code = (error.body as { error?: string } | null | undefined)?.error
    return new TeamApiError(error.status, error.body, code ?? `Request failed (${error.status})`)
  }
  return error
}

function authedClient(opts?: RequestOptions): ApiClient {
  return opts?.fetchFn ? createWebApiClient(opts.fetchFn) : getWebApiClient()
}

function publicClient(fetchFn?: typeof fetch): ApiClient {
  return fetchFn ? createWebPublicApiClient(fetchFn) : getWebPublicApiClient()
}

async function authedRequest<T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  opts?: RequestOptions,
  payload?: unknown
): Promise<T> {
  try {
    return await authedClient(opts).request<T>(path, {
      method,
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
      signal: opts?.signal,
    })
  } catch (error) {
    throw toTeamError(error)
  }
}

export function getOrganization(opts: RequestOptions = {}): Promise<OrganizationContext> {
  return authedRequest<OrganizationContext>('/api/v1/organizations/me', 'GET', opts)
}

/** GET /organizations/contexts — the caller's switchable account contexts. */
export async function listContexts(opts: RequestOptions = {}): Promise<SwitchableContext[]> {
  const { contexts } = await authedRequest<{ contexts: SwitchableContext[] }>(
    '/api/v1/organizations/contexts',
    'GET',
    opts
  )
  return contexts
}

export function createOrganization(
  name: string,
  opts: RequestOptions = {}
): Promise<CreateOrganizationResponse> {
  return authedRequest<CreateOrganizationResponse>('/api/v1/organizations', 'POST', opts, { name })
}

export function inviteMember(
  organizationId: number,
  email: string,
  idempotencyKey: string,
  opts: RequestOptions = {}
): Promise<InviteMemberResponse> {
  return authedRequest<InviteMemberResponse>(
    `/api/v1/organizations/${organizationId}/members`,
    'POST',
    opts,
    { email, idempotencyKey }
  )
}

export function getSeatPaymentStatus(
  tradeId: number,
  opts: RequestOptions = {}
): Promise<PaymentDeliveryStatusResponse> {
  return authedRequest<PaymentDeliveryStatusResponse>(
    `/api/v1/billing/team/seat-payments/${tradeId}`,
    'GET',
    opts
  )
}

export function getTeamSubscriptionPaymentStatus(
  tradeId: number,
  opts: RequestOptions = {}
): Promise<TeamSubscriptionPaymentStatusResponse> {
  return authedRequest<TeamSubscriptionPaymentStatusResponse>(
    `/api/v1/billing/team/subscription-payments/${tradeId}`,
    'GET',
    opts
  )
}

export function revokeMember(
  organizationId: number,
  memberId: number,
  opts: RequestOptions = {}
): Promise<{ ok: true }> {
  return authedRequest<{ ok: true }>(
    `/api/v1/organizations/${organizationId}/members/${memberId}`,
    'DELETE',
    opts
  )
}

export function getTeamPlan(opts: RequestOptions = {}): Promise<TeamPlanPricing> {
  return authedRequest<TeamPlanPricing>('/api/v1/billing/team/plan', 'GET', opts)
}

export function subscribeTeam(
  payload: SubscribeTeamPayload,
  opts: RequestOptions = {}
): Promise<TeamBillingResult> {
  return authedRequest<TeamBillingResult>('/api/v1/billing/team/subscribe', 'POST', opts, payload)
}

export function updateTeamSeats(
  seats: number,
  opts: RequestOptions = {}
): Promise<TeamBillingResult> {
  return authedRequest<TeamBillingResult>('/api/v1/billing/team/seats', 'PUT', opts, { seats })
}

/** Buy seats during a Team trial; `purchasedSeats` excludes the gifted seats. */
export function purchaseTrialSeats(
  purchasedSeats: number,
  opts: RequestOptions = {}
): Promise<TeamBillingResult> {
  const payload: PurchaseTrialSeatsRequest = { purchasedSeats }
  return authedRequest<TeamBillingResult>('/api/v1/billing/team/trial/seats', 'PUT', opts, payload)
}

/** What `purchaseTrialSeats` with the same count would charge now. Read-only. */
export function quoteTrialSeats(
  purchasedSeats: number,
  opts: RequestOptions = {}
): Promise<TrialSeatQuote> {
  const query: TrialSeatQuoteQuery = { purchasedSeats }
  const params = new URLSearchParams({ purchasedSeats: String(query.purchasedSeats) })
  return authedRequest<TrialSeatQuote>(
    `/api/v1/billing/team/trial/seats/quote?${params}`,
    'GET',
    opts
  )
}

export function cancelTrialContinuation(
  opts: RequestOptions = {}
): Promise<TeamContinuationResponse> {
  return authedRequest<TeamContinuationResponse>(
    '/api/v1/billing/team/trial/continuation/cancel',
    'POST',
    opts
  )
}

export function resumeTrialContinuation(
  opts: RequestOptions = {}
): Promise<TeamContinuationResponse> {
  return authedRequest<TeamContinuationResponse>(
    '/api/v1/billing/team/trial/continuation/resume',
    'POST',
    opts
  )
}

export function updateMemberAccess(
  organizationId: number,
  memberId: number,
  accessState: OrganizationMemberAccessState,
  opts: RequestOptions = {}
): Promise<UpdateMemberAccessResponse> {
  const payload: UpdateMemberAccessRequest = { accessState }
  return authedRequest<UpdateMemberAccessResponse>(
    `/api/v1/organizations/${organizationId}/members/${memberId}`,
    'PATCH',
    opts,
    payload
  )
}

/** Public — no auth needed to preview an invite. */
export async function previewInvite(token: string, fetchFn?: typeof fetch): Promise<InvitePreview> {
  try {
    return await publicClient(fetchFn).request<InvitePreview>(
      `/api/v1/organizations/invites/${token}`,
      { method: 'GET', cache: 'no-store' }
    )
  } catch (error) {
    throw toTeamError(error)
  }
}

export function acceptInvite(
  token: string,
  opts: RequestOptions = {}
): Promise<AcceptInviteResponse> {
  return authedRequest<AcceptInviteResponse>('/api/v1/organizations/invites/accept', 'POST', opts, {
    token,
  })
}

/**
 * In-app invite banner — accept a pending invite the signed-in user owns by
 * email, by `membershipId` (from `/organizations/me`'s `seatedIn.membershipId`).
 * No emailed token needed; the server enforces the email match.
 */
export function acceptPendingInvite(
  membershipId: number,
  opts: RequestOptions = {}
): Promise<AcceptInviteResponse> {
  return authedRequest<AcceptInviteResponse>(
    '/api/v1/organizations/invites/pending/accept',
    'POST',
    opts,
    { membershipId }
  )
}

/**
 * In-app invite banner — mail a 6-digit code to the signed-in account's own
 * address. Used when accept/decline answered `email_verification_required`.
 * 429 (`TeamApiError.status`) while the per-address resend window is open.
 */
export function requestEmailVerification(
  opts: RequestOptions = {}
): Promise<EmailVerificationRequestResponse> {
  return authedRequest<EmailVerificationRequestResponse>(
    '/api/v1/auth/email-verification/request',
    'POST',
    opts
  )
}

/**
 * In-app invite banner — prove the address with the mailed code. Keeps the
 * current session; a 401 carries the OTP failure `reason` in `TeamApiError.body`.
 */
export function verifyEmailVerification(
  code: string,
  opts: RequestOptions = {}
): Promise<EmailVerificationVerifyResponse> {
  return authedRequest<EmailVerificationVerifyResponse>(
    '/api/v1/auth/email-verification/verify',
    'POST',
    opts,
    { code }
  )
}

/** In-app invite banner — decline a pending invite by `membershipId`. */
export function declinePendingInvite(
  membershipId: number,
  opts: RequestOptions = {}
): Promise<{ ok: true }> {
  return authedRequest<{ ok: true }>(
    '/api/v1/organizations/invites/pending/decline',
    'POST',
    opts,
    { membershipId }
  )
}

/**
 * Public — redeem the magic-link invite (UNAUTHENTICATED). HYBRID outcome:
 * a BRAND-NEW invited email gets provisioned + accepted and returns a bearer
 * session (`outcome: 'signed_in'`); an EXISTING account returns
 * `outcome: 'needs_sign_in'` with NO token (the invite is not consumed) so the
 * caller must authenticate (OTP) and then call the authed `acceptInvite`. The
 * invite token is the capability (sent only to the invited address).
 */
export async function redeemInvite(
  token: string,
  fetchFn?: typeof fetch
): Promise<RedeemInviteResponse> {
  try {
    return await publicClient(fetchFn).request<RedeemInviteResponse>(
      '/api/v1/organizations/invites/redeem',
      { method: 'POST', body: JSON.stringify({ token }) }
    )
  } catch (error) {
    throw toTeamError(error)
  }
}
